import React, { useState } from 'react';
import { PartyPopper, Share2, Check } from 'lucide-react';
import { useTranslation } from '@/context/TranslationContext';

const SHARE_URL = 'https://joinsahiri.com';
const CONFETTI = ['#C08261', '#7DB9A1', '#D4A574', '#825743', '#F472B6', '#60A5FA', '#10B981', '#E6A57E', '#A78BFA', '#FBBF24', '#C08261', '#7DB9A1'];

function Confetti() {
  return (
    <span className="absolute left-6 top-6 pointer-events-none" aria-hidden="true">
      {CONFETTI.map((c, i) => (
        <span key={`${c}-${i}`} className="confetti-piece" style={{ '--angle': `${i * 30}deg`, '--dist': `${44 + (i % 3) * 16}px`, backgroundColor: c, animationDelay: `${(i % 4) * 60}ms` }} />
      ))}
    </span>
  );
}

function useShare(milestone) {
  const [copied, setCopied] = useState(false);
  const text = milestone.share_text || `Sahiri just passed ${milestone.latest.toLocaleString()} members. ${SHARE_URL}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Sahiri Amali', text, url: SHARE_URL }); return; } catch (e) { if (e?.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    }
  };
  return { share, copied };
}

/** Small celebration shown for 14 days after the real member count crosses 100 / 250 / 500 / 1,000 … */
export function MilestoneCelebration({ milestone, compact = false }) {
  const { t } = useTranslation();
  const { share, copied } = useShare(milestone);
  const n = milestone.latest.toLocaleString();
  const ShareIcon = copied ? Check : Share2;
  const shareLabel = copied ? t('Copied') : t('Share the moment');

  if (compact) {
    return (
      <span className="inline-flex items-center gap-2 text-xs" data-testid="milestone-celebration-compact">
        <PartyPopper className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} aria-hidden="true" />
        <span className="text-[var(--text-primary)]">{t('We just passed')} {n} {t('members.')}</span>
        <button type="button" onClick={share} className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline" data-testid="milestone-share-btn-compact">
          <ShareIcon className="w-3 h-3" aria-hidden="true" /> {shareLabel}
        </button>
      </span>
    );
  }

  return (
    <div className="relative flex flex-col gap-3 px-4 py-3.5 rounded-2xl border animate-[fadeIn_0.6s_ease-out]" style={{ borderColor: 'rgba(192,130,97,0.45)', backgroundColor: 'rgba(192,130,97,0.08)' }} data-testid="milestone-celebration" role="status">
      <Confetti />
      <div className="flex items-start gap-3">
        <PartyPopper className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} aria-hidden="true" />
        <p className="text-sm text-[var(--text-primary)]">
          <span className="font-semibold">{t('We just passed')} {n} {t('members.')}</span>{' '}
          <span className="text-[var(--text-secondary)]">{t('Thank you for being part of this.')}</span>
          {milestone.preview && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--text-secondary)]/60" data-testid="milestone-preview-tag">{t('preview')}</span>}
        </p>
      </div>
      <button type="button" onClick={share} className="self-start ml-7 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors" data-testid="milestone-share-btn">
        <ShareIcon className="w-3.5 h-3.5" aria-hidden="true" /> {shareLabel}
      </button>
    </div>
  );
}
